import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, SkipBack, SkipForward, Rewind, Undo2, Redo2, Scissors, Trash2, ZoomIn, ZoomOut, Repeat, Mic } from 'lucide-react';
import { usePlayback } from '../hooks/usePlayback';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { useLoopStore } from '../store/loopStore';
import { msToTimecode } from '../lib/timeUtils';
import { EFFECT_CATALOG, type EffectType } from '../engine/effectsManager';
import { useEffectClipDialogStore } from '../store/effectClipDialogStore';
import { RecordingDialog } from './RecordingDialog';
import { VUMeter } from './VUMeter';

export function TimelineControls() {
  const { t } = useTranslation();
  const { playing, currentTimeMs, togglePlayback, stepForward, stepBackward, goToStart } = usePlayback();
  const fps = useEditorStore((s) => s.project.fps);
  const zoomIn = useEditorStore((s) => s.zoomIn);
  const zoomOut = useEditorStore((s) => s.zoomOut);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.canUndo());
  const canRedo = useEditorStore((s) => s.canRedo());
  const selectedClipId = useEditorStore((s) => s.timeline.selectedClipId);
  const splitClip = useEditorStore((s) => s.splitClip);
  const removeClip = useEditorStore((s) => s.removeClip);
  const selectClip = useEditorStore((s) => s.selectClip);
  const hasClips = useEditorStore((s) => s.project.tracks.some((t) => t.clips.length > 0));
  const loopEnabled = useLoopStore((s) => s.loopEnabled);
  const toggleLoop = useLoopStore((s) => s.toggleLoop);
  const selectionStart = useLoopStore((s) => s.selectionStart);
  const selectionEnd = useLoopStore((s) => s.selectionEnd);
  const hasSelection = selectionStart !== null && selectionEnd !== null && Math.abs(selectionEnd - selectionStart) > 50;

  const [showRecording, setShowRecording] = useState(false);
  const [showEffectMenu, setShowEffectMenu] = useState(false);
  const effectMenuRef = useRef<HTMLDivElement>(null);
  const effectBtnRef = useRef<HTMLButtonElement>(null);
  const [effectMenuPos, setEffectMenuPos] = useState<{ top: number; left: number } | null>(null);

  // Compute position when menu opens
  useEffect(() => {
    if (showEffectMenu && effectBtnRef.current) {
      const rect = effectBtnRef.current.getBoundingClientRect();
      setEffectMenuPos({ top: rect.top, left: rect.left });
    } else {
      setEffectMenuPos(null);
    }
  }, [showEffectMenu]);

  // Close effect menu on click outside
  useEffect(() => {
    if (!showEffectMenu) return;
    function handleClick(e: MouseEvent) {
      if (effectMenuRef.current && !effectMenuRef.current.contains(e.target as Node)) {
        setShowEffectMenu(false);
      }
    }
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [showEffectMenu]);

  const handleAddEffect = (effectType: string) => {
    const loop = useLoopStore.getState();
    if (loop.selectionStart === null || loop.selectionEnd === null) return;

    const selStart = Math.min(loop.selectionStart, loop.selectionEnd);
    const selEnd = Math.max(loop.selectionStart, loop.selectionEnd);

    const catalog = EFFECT_CATALOG[effectType as EffectType];
    useEffectClipDialogStore.getState().openForAdd(
      effectType, selStart, selEnd, { ...catalog.defaults },
    );
    setShowEffectMenu(false);
  };

  const handleSplit = () => {
    if (!selectedClipId) return;
    const timeMs = usePlayheadStore.getState().currentTimeMs;
    splitClip(selectedClipId, timeMs);
  };

  const handleDelete = () => {
    if (!selectedClipId) return;
    removeClip(selectedClipId);
    selectClip(null);
  };

  const btnClass = "p-1.5 rounded-[var(--radius-chip)] hover:bg-[var(--user-nav-hover-bg)] text-[var(--user-page-text)] opacity-70 hover:opacity-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed";

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-[#252529] border-b border-[#2A2A2E]">
      <div className="flex items-center gap-2">
        <button onClick={undo} disabled={!canUndo} className={btnClass} title={t('editor.undo', 'Undo')}>
          <Undo2 size={16} />
        </button>
        <button onClick={redo} disabled={!canRedo} className={btnClass} title={t('editor.redo', 'Redo')}>
          <Redo2 size={16} />
        </button>
        <div className="w-px h-5 bg-[var(--user-page-card-border)] mx-1" />
        <button onClick={goToStart} disabled={!hasClips} className={btnClass} title={t('editor.goToStart', 'Go to start')}>
          <Rewind size={16} />
        </button>
        <button onClick={stepBackward} disabled={!hasClips} className={btnClass} title={t('editor.stepBack', 'Step back')}>
          <SkipBack size={16} />
        </button>
        <button
          onClick={togglePlayback}
          disabled={!hasClips}
          className={`p-2 rounded-full bg-[var(--user-page-accent)] text-[var(--user-page-bg)] hover:brightness-110 transition-all disabled:opacity-30 disabled:cursor-not-allowed`}
          title={playing ? t('editor.pause', 'Pause') : t('editor.play', 'Play')}
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button onClick={stepForward} disabled={!hasClips} className={btnClass} title={t('editor.stepForward', 'Step forward')}>
          <SkipForward size={16} />
        </button>
        <div className="w-px h-5 bg-[var(--user-page-card-border)] mx-1" />
        <button onClick={handleSplit} disabled={!selectedClipId} className={btnClass} title={`${t('editor.split', 'Split')} ⌘B`}>
          <Scissors size={16} />
        </button>
        <button onClick={handleDelete} disabled={!selectedClipId} className={btnClass} title={`${t('editor.delete', 'Delete')} ⌫`}>
          <Trash2 size={16} />
        </button>
        <div className="w-px h-5 bg-[var(--user-page-card-border)] mx-1" />
        <button
          onClick={toggleLoop}
          className={`${btnClass} ${loopEnabled ? '!opacity-100 !text-[var(--user-page-accent)]' : ''}`}
          title={`${t('editor.loopToggle', 'Loop')} L`}
        >
          <Repeat size={16} />
        </button>
        <div className="relative">
          <button
            ref={effectBtnRef}
            onClick={() => setShowEffectMenu(!showEffectMenu)}
            disabled={!hasSelection}
            className={`${btnClass} ${showEffectMenu ? '!opacity-100 !text-[#A855F7]' : ''}`}
            title={t('editor.addEffect', 'Add Effect')}
          >
            <span className="text-xs font-bold">FX</span>
          </button>
          {showEffectMenu && effectMenuPos && (
            <div
              ref={effectMenuRef}
              className="fixed z-[100] min-w-[180px] py-1 rounded-lg bg-[#1C1C1E] border border-[#3A3A3C] shadow-xl shadow-black/50 max-h-64 overflow-y-auto"
              style={{ left: effectMenuPos.left, bottom: window.innerHeight - effectMenuPos.top + 4 }}
            >
              {Object.entries(
                Object.entries(EFFECT_CATALOG).reduce<Record<string, { key: string; label: string }[]>>(
                  (acc, [key, val]) => { (acc[val.category] ??= []).push({ key, label: val.label }); return acc; },
                  {},
                ),
              ).map(([category, effects]) => (
                <div key={category}>
                  <div className="px-3 py-1 text-[10px] text-[#636366] font-medium uppercase tracking-wider">
                    {category}
                  </div>
                  {effects.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleAddEffect(key)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-start text-[#E5E5EA] hover:bg-[#2C2C2E] cursor-pointer"
                    >
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Timecode */}
      <div className="font-mono text-sm text-[var(--user-page-text)] opacity-80 tabular-nums">
        {msToTimecode(currentTimeMs, fps)}
      </div>

      {/* Record + Zoom */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => setShowRecording(true)}
          className="p-1.5 rounded hover:bg-[var(--user-nav-hover-bg)] text-red-400 opacity-70 hover:opacity-100 transition-all"
          title={t('editor.record', 'Record')}
        >
          <Mic size={16} />
        </button>
        <VUMeter orientation="horizontal" size="compact" />
        <div className="w-px h-5 bg-[var(--user-page-card-border)] mx-1" />
        <button onClick={zoomOut} className={btnClass} title={`${t('editor.zoomOut', 'Zoom out')} ⌘-`}>
          <ZoomOut size={16} />
        </button>
        <button onClick={zoomIn} className={btnClass} title={`${t('editor.zoomIn', 'Zoom in')} ⌘+`}>
          <ZoomIn size={16} />
        </button>
      </div>

      {showRecording && (
        <RecordingDialog onClose={() => setShowRecording(false)} />
      )}
    </div>
  );
}
